import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from 'mdi-react/CloseIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import Api from '../../../../util/api';
import useConfig from '../../../../util/useConfig';
import { partial } from '../../../../util/translation/translation';
import FormBox from '../../../../shared/components/custom/FormBox';

export const DEFAULT_GUEST_FIELDS = { name: 'off', email: 'off' };
const GUEST_FIELD_SETTINGS = ['off', 'optional', 'required'];

// Questions for a series that doesn't exist yet: picked from the club's
// question library and kept here until the series is created, then saved
// to the question service under domainId = seriesId. The full question
// builder is on the series page's Questions tab.
const SeriesQuestionsPicker = ({ value, onChange }) => {
  const p = partial('EventSeries');
  const qT = partial('question');
  const { id: clubId } = useConfig();
  const [library, setLibrary] = useState([]);
  const [search, setSearch] = useState('');
  const { question: picked, guestFields } = value;

  useEffect(() => {
    Api.questions
      .fetchSharedQuestions(clubId)
      .then(({ data }) => setLibrary(Array.isArray(data) ? data : []))
      .catch(() => setLibrary([]));
  }, [clubId]);

  const isPicked = (questionId) => picked.some((q) => q.id === questionId);
  const visibleLibrary = library.filter(
    (item) =>
      !isPicked(item.id) &&
      (item.question?.question || '')
        .toLowerCase()
        .includes(search.trim().toLowerCase())
  );

  const add = (item) =>
    onChange({
      ...value,
      question: [...picked, { id: item.id, ...item.question }],
    });
  const remove = (questionId) =>
    onChange({ ...value, question: picked.filter((q) => q.id !== questionId) });
  const setGuestField = (field, setting) =>
    onChange({ ...value, guestFields: { ...guestFields, [field]: setting } });

  const guestFieldLabel = {
    off: qT('guestFieldOff'),
    optional: qT('guestFieldOptional'),
    required: qT('guestFieldRequired'),
  };

  return (
    <FormBox title={qT('questions')}>
      <div className="series-questions">
        <p className="series-form__muted">{p('questionsHint')}</p>
        {picked.length > 0 && (
          <ol className="series-questions__picked">
            {picked.map((q) => (
              <li key={q.id}>
                <span>{q.question}</span>
                <button
                  type="button"
                  className="series-chip__remove"
                  aria-label={p('removeQuestion', { question: q.question })}
                  onClick={() => remove(q.id)}
                >
                  <CloseIcon size={14} />
                </button>
              </li>
            ))}
          </ol>
        )}
        <label className="qb__search series-questions__search">
          <MagnifyIcon size={18} aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={qT('searchQuestions')}
            aria-label={qT('searchQuestions')}
          />
        </label>
        <ul className="series-questions__library">
          {visibleLibrary.map((item) => (
            <li key={item.id}>
              <span>{item.question?.question}</span>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-0"
                onClick={() => add(item)}
              >
                {p('add')}
              </button>
            </li>
          ))}
          {!visibleLibrary.length && (
            <li className="text-muted">{qT('noLibraryQuestions')}</li>
          )}
        </ul>
        <div className="series-questions__guests">
          {[
            ['name', qT('guestName')],
            ['email', qT('guestEmail')],
          ].map(([field, label]) => (
            <div className="qb-guest-field" key={field}>
              <div className="qb-guest-field__text">
                <span
                  className="qb-guest-field__label"
                  id={`seriesGuestField_${field}`}
                >
                  {label}
                </span>
              </div>
              <div
                className="qb-segment"
                role="radiogroup"
                aria-labelledby={`seriesGuestField_${field}`}
              >
                {GUEST_FIELD_SETTINGS.map((setting) => (
                  <button
                    key={setting}
                    type="button"
                    role="radio"
                    aria-checked={guestFields[field] === setting}
                    className={`qb-segment__btn${
                      guestFields[field] === setting
                        ? ' qb-segment__btn--on'
                        : ''
                    }`}
                    onClick={() => setGuestField(field, setting)}
                  >
                    {guestFieldLabel[setting]}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <small className="text-muted d-block mt-2">
          {p('questionsAfterCreate')}
        </small>
      </div>
    </FormBox>
  );
};

SeriesQuestionsPicker.propTypes = {
  value: PropTypes.shape({
    question: PropTypes.arrayOf(PropTypes.shape({})),
    guestFields: PropTypes.shape({}),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SeriesQuestionsPicker;
